import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Paper,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Typography,
} from '@mui/material';
import { differenceInDays } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { GridColumnVisibilityModel } from '@thingslog/ui-components/src/DataGrid';
import { GridRowParams, useToast } from '@thingslog/ui-components';
import {
  AggregationOptions,
  Device,
  DeviceGroupDto,
  ExportFormats,
  Port,
  ReadingsExportDto,
} from '@thingslog/repositories';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import Header from '../../components/header';
import {
  MultiSelectAutocompleteOption,
  MultiSelectAutocompleteWithActions,
} from '../../components/MultiSelectAutocomplete';
import TitleHeader from '../../components/TitleHeader/TitleHeader';
import CountersTable from './CountersTable';
import { ReduxState } from '../../reducers';
import DeviceSensorsDto from '@thingslog/repositories/src/deviceSensorsDto/DeviceSensorsDto';
import {
  deviceGroupsQueryClient,
  devicesQueryClient,
  initialConfigQueryClient,
  multipleReadingsExportQueryClient,
} from '../../clients/ReactQueryClients/ReactQueryClients';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import getDefaultSensorName from '../../common/SensorNameHelper';
import { GaEventAction } from '../../common/GaEventAction';
import { GaEventCategory } from '../../common/GaEventCategory';
import GoogleAnalyticsService from '../../common/GoogleAnalyticsService';
import * as actions from '../../actions/index';
import ConnectedDatePicker from '../../components/PeriodPicker/ConnectedDatePicker';
import { PickerPeriod } from '../../model/Pickers/PickerPeriod';
import JwtValidator from '../../common/JwtValidator';

const CountersExport: FC<CountersExportProps> = () => {
  const { useDevicesData } = useMemo(() => devicesQueryClient, []);
  const { useDeviceGroupsData } = useMemo(() => deviceGroupsQueryClient, []);
  const { useDevicePortsConfigData } = useMemo(() => initialConfigQueryClient, []);
  const {
    useMultipleReadingsExportCSV,
    useMultipleReadingsExportExcel,
    useMultipleReadingsExportJSON,
  } = useMemo(() => multipleReadingsExportQueryClient, []);
  const { width } = useWindowDimensions();
  const { t } = useTranslation();
  const { toast } = useToast();
  const dispatch = useDispatch();
  const { deviceNumber } = useParams();
  const { determineCompanyIdForUserRole } = useMemo(() => new JwtValidator(), []);

  const companyId = useSelector((state: ReduxState) => state.company.id);
  const currentCompanyId = useMemo(() => determineCompanyIdForUserRole(companyId), [companyId]);
  const selectedDevice = useSelector((state: ReduxState) => state.dev.selectedDevice);
  const selectedDeviceNumber = useSelector((state: ReduxState) => state.dev.devices[0] || null);
  const toDateTz = useSelector((state: ReduxState) => state.period.toDateTz);
  const fromDateTz = useSelector((state: ReduxState) => state.period.fromDateTz);
  const isSingleDeviceMode = !!deviceNumber;
  const isMultiDeviceMode = !isSingleDeviceMode;

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    deviceName: true,
    deviceNumber: false,
    sensorIndex: true,
    previousCounter: true,
    dt: true,
    delta: true,
  });
  const [aggregationPeriod, setAggregationPeriod] = useState<AggregationOptions | null>(null);
  const [exportFormat, setExportFormat] = useState<ExportFormats>('Excel');
  const [data, setData] = useState<ReadingsExportDto[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [selectedDeviceGroups, setSelectedDeviceGroups] = useState<number[]>([]);
  const [selectedSensorIndexes, setSelectedSensorIndexes] = useState<number[]>([]);
  const [enabledPorts, setEnabledPorts] = useState<Record<number, Port>>({});
  const [isExceedingRequestLimit, setIsExceedingRequestLimit] = useState<boolean>(false);
  const [selectedDeviceSnackbar, setSelectedDeviceSnackbar] = useState<{
    open: boolean;
    deviceNumber: string | null;
    key: number;
  }>({
    open: false,
    deviceNumber: null,
    key: 0,
  });

  const defaultSensorIndexes: number[] = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
  const aggregationSelectOptions: Record<AggregationOptions | 'NONE', string> = {
    NONE: t('consumption_export_aggregation_options_none'),
    DAILY: t('consumption_export_aggregation_options_daily'),
    WEEKLY: t('consumption_export_aggregation_options_weekly'),
    MONTHLY: t('consumption_export_aggregation_options_monthly'),
    YEARLY: t('consumption_export_aggregation_options_yearly'),
  };

  const devicesQuery = useDevicesData(currentCompanyId, 0, 9999, null, null, {
    enabled: currentCompanyId !== null && isMultiDeviceMode,
    onError: () => {
      toast({ message: t('counters_export_error_alert_message'), type: 'error', duration: 5000 });
    },
    refetchOnWindowFocus: false,
  });

  const deviceGroupsQuery = useDeviceGroupsData(currentCompanyId, {
    enabled: currentCompanyId !== null && isMultiDeviceMode,
    onError: () => {
      toast({ message: t('counters_export_error_alert_message'), type: 'error', duration: 5000 });
    },
    refetchOnWindowFocus: false,
  });

  const { isSuccess: isDevicePortsConfigSuccess } = useDevicePortsConfigData(deviceNumber!, {
    enabled: !!deviceNumber,
    onSuccess: (portsConfig: Record<number, Port>) => {
      let enabledPortsFromConfig: Record<number, Port> = {};

      Object.entries(portsConfig).forEach(([sensorIndex, port]: [string, Port]) => {
        if (port.enabled) {
          enabledPortsFromConfig[sensorIndex] = port;
        }
      });

      setEnabledPorts(enabledPortsFromConfig);
      setSelectedSensorIndexes(
        Object.keys(enabledPortsFromConfig)
          .map((sensorIndex: string) => Number(sensorIndex))
          .sort((a: number, b: number) => a - b)
      );
    },
    onError: () => {
      toast({ message: t('error_cannot_fetch_ports_config'), type: 'error', duration: 5000 });
    },
  });

  const {
    mutate: jsonMutate,
    isLoading: isJSONLoading,
    isSuccess: isJSONSuccess,
  } = useMultipleReadingsExportJSON({
    onSuccess: (jsonData: ReadingsExportDto[]) => {
      setData(jsonData);
    },
    onError: () => {
      toast({ message: t('counters_export_error_alert_message'), type: 'error', duration: 5000 });
    },
  });

  const {
    mutate: exportMutateCSV,
    isLoading: isExportingCSV,
    isSuccess: isExportCSVSuccess,
  } = useMultipleReadingsExportCSV({
    onSuccess: (exportData: ArrayBuffer) => {
      const fileName = `ReadingsExport-${exportFormat}-from-${fromDateTz.toISOString()}-to-${toDateTz.toISOString()}.zip`;
      const url = window.URL.createObjectURL(new Blob([exportData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
    },
    onError: () => {
      toast({ message: t('counters_export_error_alert_message'), type: 'error', duration: 5000 });
    },
  });

  const {
    mutate: exportMutateExcel,
    isLoading: isExportingExcel,
    isSuccess: isExportExcelSuccess,
  } = useMultipleReadingsExportExcel({
    onSuccess: (exportData: ArrayBuffer) => {
      const fileName = `ReadingsExport-${exportFormat}-from-${fromDateTz.toISOString()}-to-${toDateTz.toISOString()}.zip`;
      const url = window.URL.createObjectURL(new Blob([exportData]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(link.href);
    },
    onError: () => {
      toast({ message: t('counters_export_error_alert_message'), type: 'error', duration: 5000 });
    },
  });

  useEffect(() => {
    if (deviceNumber) {
      setSelectedDevices([deviceNumber]);
      setSelectedDeviceGroups([]);
      jsonMutate({
        body: {
          deviceSensors: [
            {
              deviceNumber,
              sensorIndexes: defaultSensorIndexes,
            },
          ],
          deviceGroupIds: [],
          fromDate: fromDateTz,
          toDate: toDateTz,
        },
        fetchType: aggregationPeriod || undefined,
      });
    } else {
      setSelectedDevices([]);
      setSelectedDeviceGroups([]);
      setSelectedSensorIndexes(defaultSensorIndexes);
    }

    setColumnVisibilityModel((prevModel: GridColumnVisibilityModel) =>
      deviceNumber
        ? { ...prevModel, deviceName: false, deviceNumber: false }
        : { ...prevModel, deviceName: true }
    );
  }, [deviceNumber]);

  useEffect(() => {
    let daysDifference = differenceInDays(toDateTz, fromDateTz);
    let selectedDevicesCount = selectedDevices.length;

    if (daysDifference * selectedDevicesCount > 900 && isMultiDeviceMode) {
      setIsExceedingRequestLimit(true);
    } else {
      setIsExceedingRequestLimit(false);
    }
  }, [selectedDevices, fromDateTz, toDateTz, isMultiDeviceMode]);

  useEffect(() => {
    const isMobile = width < 1024;
    setColumnVisibilityModel((prevModel: GridColumnVisibilityModel) => ({
      ...prevModel,
      sensorIndex: !isMobile,
      previousCounter: !isMobile,
      dt: !isMobile,
      delta: !isMobile,
    }));
  }, [width]);

  const deviceOptions = useMemo((): MultiSelectAutocompleteOption<string>[] => {
    if (!devicesQuery.data) {
      return [];
    }

    return devicesQuery.data.content.map((device: Device) => {
      const label = device.name ? `${device.name} (${device.number})` : device.number;
      return {
        value: device.number,
        label,
        searchText: `${device.number} ${device.name || ''}`.toLowerCase(),
      };
    });
  }, [devicesQuery.data]);

  const deviceGroupOptions = useMemo((): MultiSelectAutocompleteOption<number>[] => {
    if (!deviceGroupsQuery.data) {
      return [];
    }

    return deviceGroupsQuery.data.map((group: DeviceGroupDto) => ({
      value: group.deviceGroupId,
      label: group.deviceGroupName,
      searchText: `${group.deviceGroupName} ${group.deviceGroupId}`.toLowerCase(),
    }));
  }, [deviceGroupsQuery.data]);

  const sensorOptions = useMemo((): MultiSelectAutocompleteOption<number>[] => {
    return Object.entries(enabledPorts)
      .map(([sensorIndex, port]: [string, Port]) => {
        const numericSensorIndex = Number(sensorIndex);
        const sensorName = port.sensor.name || getDefaultSensorName(sensorIndex);

        return {
          value: numericSensorIndex,
          label: sensorName,
          searchText: `${sensorName} ${sensorIndex}`.toLowerCase(),
        };
      })
      .sort(
        (
          firstOption: MultiSelectAutocompleteOption<number>,
          secondOption: MultiSelectAutocompleteOption<number>
        ) => firstOption.value - secondOption.value
      );
  }, [enabledPorts]);

  const formDeviceSensorsOptions = (): DeviceSensorsDto[] => {
    return selectedDevices.map((currentDeviceNumber: string) => ({
      deviceNumber: currentDeviceNumber,
      sensorIndexes: (isSingleDeviceMode ? selectedSensorIndexes : defaultSensorIndexes).sort(
        (a: number, b: number) => a - b
      ),
    }));
  };

  const handleGoogleAnalyticsEvent = (eventType: 'search' | 'download'): void => {
    switch (eventType) {
      case 'search': {
        if (deviceNumber) {
          GoogleAnalyticsService.triggerEvent(
            GaEventCategory.READINGS_PAGE,
            GaEventAction.READING_PAGE_SEARCH_CLICK
          );
        } else {
          GoogleAnalyticsService.triggerEvent(
            GaEventCategory.EXPORT_PAGE,
            GaEventAction.EXPORT_PAGE_SEARCH_CLICK
          );
        }
        break;
      }
      case 'download': {
        if (deviceNumber) {
          GoogleAnalyticsService.triggerEvent(
            GaEventCategory.READINGS_PAGE,
            GaEventAction.READING_PAGE_DOWNLOAD_CLICK,
            exportFormat
          );
        } else {
          GoogleAnalyticsService.triggerEvent(
            GaEventCategory.EXPORT_PAGE,
            GaEventAction.EXPORT_PAGE_DOWNLOAD_CLICK,
            exportFormat
          );
        }
      }
    }
  };

  const downloadHandler = (): void => {
    const deviceSensors = formDeviceSensorsOptions();

    switch (exportFormat) {
      case 'CSV':
        exportMutateCSV({
          body: {
            deviceSensors,
            deviceGroupIds: selectedDeviceGroups,
            fromDate: fromDateTz,
            toDate: toDateTz,
          },
          fetchType: aggregationPeriod || undefined,
        });
        break;
      case 'Excel':
        exportMutateExcel({
          body: {
            deviceSensors,
            deviceGroupIds: selectedDeviceGroups,
            fromDate: fromDateTz,
            toDate: toDateTz,
          },
          fetchType: aggregationPeriod || undefined,
        });
        break;
      default:
        break;
    }

    handleGoogleAnalyticsEvent('download');
  };

  const loadJsonDataHandler = (): void => {
    const deviceSensors = formDeviceSensorsOptions();

    jsonMutate({
      body: {
        deviceSensors,
        deviceGroupIds: selectedDeviceGroups,
        fromDate: fromDateTz,
        toDate: toDateTz,
      },
      fetchType: aggregationPeriod || undefined,
    });

    handleGoogleAnalyticsEvent('search');
  };

  const disableButtons = (): boolean => {
    const isActionLoading = isJSONLoading || isExportingCSV || isExportingExcel;
    const isMultiSelectionLoading =
      isMultiDeviceMode && (devicesQuery.isLoading || deviceGroupsQuery.isLoading);

    return (
      isActionLoading || isMultiSelectionLoading || (isMultiDeviceMode && isExceedingRequestLimit)
    );
  };

  const hasSelection =
    isSingleDeviceMode || selectedDevices.length > 0 || selectedDeviceGroups.length > 0;
  const hasCompletedRequest = isJSONSuccess || isExportCSVSuccess || isExportExcelSuccess;
  const showInitialAlerts =
    isMultiDeviceMode &&
    !isJSONLoading &&
    !isExportingCSV &&
    !isExportingExcel &&
    !hasCompletedRequest;
  const showEmptySelectionAlert = showInitialAlerts && !hasSelection;
  const showReadyAlert = showInitialAlerts && hasSelection;

  const handleSelectedDeviceNumberChange = (deviceNumberValue: string | null): void => {
    dispatch(actions.devSelected(deviceNumberValue ? [deviceNumberValue] : [], 'radio'));
  };

  const handleCountersRowClick = (params: GridRowParams<ReadingsExportDto>): void => {
    if (isSingleDeviceMode) {
      return;
    }

    const clickedDeviceNumber = params.row.deviceNumber || null;
    handleSelectedDeviceNumberChange(clickedDeviceNumber);

    if (clickedDeviceNumber) {
      setSelectedDeviceSnackbar(
        (currentValue: { open: boolean; deviceNumber: string | null; key: number }) => ({
          open: true,
          deviceNumber: clickedDeviceNumber,
          key: currentValue.key + 1,
        })
      );
    }
  };

  return (
    <Header>
      <Container>
        <TitleHeader
          className="mb-4"
          title={deviceNumber ? t('counters_readings_header') : t('counters_export_header')}
          deviceNumber={deviceNumber && selectedDevice && selectedDevice.number}
          deviceName={deviceNumber && selectedDevice && selectedDevice.name}
          customerInfo={deviceNumber && selectedDevice && selectedDevice.customerInfo}
        />
        <section className="mb-5">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col lg:flex-row justify-between gap-3 flex-wrap">
                <div className="flex flex-col lg:flex-row gap-3">
                  <ConnectedDatePicker
                    period={PickerPeriod.FROM}
                    dateDifference={isSingleDeviceMode ? undefined : 30}
                    className="w-full lg:w-[200px]"
                  />
                  <ConnectedDatePicker
                    period={PickerPeriod.TO}
                    dateDifference={isSingleDeviceMode ? undefined : 30}
                    className="w-full lg:w-[200px]"
                  />
                  <FormControl size="small" className="w-full lg:w-40">
                    <InputLabel>{t('consumption_export_aggregation_period')}</InputLabel>
                    <Select
                      value={aggregationPeriod || 'NONE'}
                      label={t('consumption_export_aggregation_period')}
                      onChange={(event: SelectChangeEvent<AggregationOptions | 'NONE'>): void => {
                        setAggregationPeriod(
                          event.target.value !== 'NONE'
                            ? (event.target.value as AggregationOptions)
                            : null
                        );
                      }}
                    >
                      {Object.entries(aggregationSelectOptions).map(
                        ([key, value]: [string, string]) => (
                          <MenuItem key={key} value={key}>
                            {value}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    startIcon={isJSONLoading ? <CircularProgress size={15} /> : <SearchIcon />}
                    disabled={disableButtons()}
                    onClick={loadJsonDataHandler}
                    className="w-full lg:w-auto"
                  >
                    {t('counters_export_search')}
                  </Button>
                </div>
                <div className="flex flex-col gap-3 lg:flex-row">
                  <FormControl size="small" className="w-full lg:w-28">
                    <InputLabel>{t('counters_export_format')}</InputLabel>
                    <Select
                      value={exportFormat}
                      label={t('counters_export_format')}
                      onChange={(e: SelectChangeEvent<ExportFormats>): void => {
                        setExportFormat(e.target.value as ExportFormats);
                      }}
                    >
                      <MenuItem value={'CSV'}>CSV</MenuItem>
                      <MenuItem value={'Excel'}>Excel</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    startIcon={
                      isExportingCSV || isExportingExcel ? (
                        <CircularProgress size={15} />
                      ) : (
                        <FileDownloadIcon />
                      )
                    }
                    disabled={disableButtons()}
                    onClick={downloadHandler}
                    className="w-full lg:w-auto"
                  >
                    {t('counters_export_download')}
                  </Button>
                </div>
              </div>
              {isSingleDeviceMode && isDevicePortsConfigSuccess && (
                <div className="w-full">
                  <MultiSelectAutocompleteWithActions
                    label={t('sensor')}
                    placeholder={t('sensor')}
                    options={sensorOptions}
                    selectedValues={selectedSensorIndexes}
                    setSelectedValues={setSelectedSensorIndexes}
                    disabled={disableButtons()}
                    loading={false}
                  />
                </div>
              )}
            </div>
            {isMultiDeviceMode && isExceedingRequestLimit && (
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>{t('counters_export_maximum_capacity_error_message')}</strong>{' '}
                  {t('counters_export_maximum_capacity_suggestion_message')}
                </Typography>
              </Alert>
            )}
            {isMultiDeviceMode && (
              <>
                <div className="border-t border-slate-200" />
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div className="w-full">
                    <MultiSelectAutocompleteWithActions
                      label={t('counter_export_select_devices_label')}
                      placeholder={t('counter_export_select_devices_label')}
                      options={deviceOptions}
                      selectedValues={selectedDevices}
                      setSelectedValues={setSelectedDevices}
                      disabled={disableButtons()}
                      loading={devicesQuery.isLoading}
                    />
                  </div>
                  <div className="w-full">
                    <MultiSelectAutocompleteWithActions
                      label={t('report_config_form_device_groups')}
                      placeholder={t('report_config_form_device_groups')}
                      options={deviceGroupOptions}
                      selectedValues={selectedDeviceGroups}
                      setSelectedValues={setSelectedDeviceGroups}
                      disabled={disableButtons()}
                      loading={deviceGroupsQuery.isLoading}
                    />
                  </div>
                </div>
                {showEmptySelectionAlert && (
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>{t('counters_export_empty_selection_title')}</strong>{' '}
                      {t('counters_export_empty_selection_message')}
                    </Typography>
                  </Alert>
                )}
                {showReadyAlert && (
                  <Alert severity="success">
                    <Typography variant="body2">
                      <strong>{t('counters_export_ready_title')}</strong>{' '}
                      {t('counters_export_ready_message', {
                        devicesCount: selectedDevices.length,
                        groupsCount: selectedDeviceGroups.length,
                      })}
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </div>
        </section>
        <section>
          <div className="w-full my-5">
            {isJSONLoading ? (
              <CircularProgress />
            ) : (
              <>
                {isJSONSuccess && (
                  <CountersTable
                    rows={data}
                    columnVisibilityModel={columnVisibilityModel}
                    onColumnVisibilityModelChange={(newModel: GridColumnVisibilityModel): void =>
                      setColumnVisibilityModel(newModel)
                    }
                    density="compact"
                    selectedDeviceNumber={selectedDeviceNumber}
                    showSelection={isMultiDeviceMode}
                    getRowId={(row: ReadingsExportDto): string =>
                      row.deviceNumber + row.sensorIndex + row.date
                    }
                    initialState={{ sorting: { sortModel: [{ field: 'date', sort: 'desc' }] } }}
                    onRowClick={isMultiDeviceMode ? handleCountersRowClick : undefined}
                  />
                )}
              </>
            )}
          </div>
        </section>
      </Container>
      <Snackbar
        key={selectedDeviceSnackbar.key}
        open={selectedDeviceSnackbar.open}
        autoHideDuration={3000}
        onClose={(): void =>
          setSelectedDeviceSnackbar(
            (currentValue: { open: boolean; deviceNumber: string | null; key: number }) => ({
              ...currentValue,
              open: false,
            })
          )
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={(): void =>
            setSelectedDeviceSnackbar(
              (currentValue: { open: boolean; deviceNumber: string | null; key: number }) => ({
                ...currentValue,
                open: false,
              })
            )
          }
          severity="success"
          sx={{ width: '100%' }}
        >
          {selectedDeviceSnackbar.deviceNumber
            ? t('counters_export_selected_device_toast', {
                deviceNumber: selectedDeviceSnackbar.deviceNumber,
              })
            : ''}
        </Alert>
      </Snackbar>
    </Header>
  );
};

interface CountersExportProps {}

export default CountersExport;
